import { useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useDraggable } from "@dnd-kit/core";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { familyToken } from "./color-tokens";
import { CropPopoverContent } from "./CropPopoverContent";
import { stageLabel } from "@/components/StageIcon";
import VeggieIcon from "@/components/VeggieIcon";

/**
 * CropChip — visual + draggable crop piece + click-to-open info popover.
 *
 * Variants:
 *   - "cell": rendered inside an anchor GridCell (compact, clipped, ×N badge)
 *   - "palette": rendered in the sidebar (full crop name + count + family)
 *   - "overlay": rendered in DndContext's DragOverlay during a drag
 *
 * dnd-kit's PointerSensor with distance:6 means quick clicks (no movement)
 * fire onClick — which opens the Popover — while drags >6px do not.
 */
export function CropChip({ crop, variant = "cell", draggable = false, dragId, onEdit, onDelete }) {
    const [popoverOpen, setPopoverOpen] = useState(false);

    const family = crop?.rotation_family || "other";
    const token = familyToken(family);

    const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
        id: dragId || `chip-${crop.id}`,
        data: {
            cropId: crop.id,
            fromPosition: crop.position || null,
            crop,
        },
        disabled: !draggable,
    });

    const base = `inline-flex items-center gap-1.5 ${token.bg} ${token.border} ${token.text}
                  border rounded-md font-mono-grid transition-colors`;
    const dragCls = draggable ? "cursor-grab active:cursor-grabbing select-none touch-none" : "";
    const draggingCls = isDragging ? "opacity-30" : "";

    // Overlay variant — pure visual (used inside DragOverlay during drag)
    if (variant === "overlay") {
        return (
            <div className={`${base} px-2 h-9 text-xs font-medium`}>
                <VeggieIcon cropName={crop.crop_name} stage={crop.stage} size={36} />
                <span>{crop.crop_name}</span>
                <span className="text-[10px] opacity-75">×{crop.plant_count || 1}</span>
            </div>
        );
    }

    // Body — what the user actually sees & drags
    let body;
    if (variant === "palette") {
        body = (
            <div
                ref={setNodeRef}
                {...listeners}
                {...attributes}
                data-testid={`chip-palette-${crop.id}`}
                aria-roledescription="crop piece"
                aria-label={`${crop.crop_name}${crop.variety ? ` ${crop.variety}` : ""}, ${crop.plant_count} plants — drag to place, click to view details`}
                className={`${base} ${dragCls} ${draggingCls} px-3 py-2 w-full justify-between text-sm hover:brightness-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--vm-sage)]`}
            >
                <span className="flex items-center gap-2 min-w-0">
                    <VeggieIcon cropName={crop.crop_name} stage={crop.stage} size={40} className="shrink-0" />
                    <span className="truncate">
                        {crop.crop_name}
                        {crop.variety && <span className="italic opacity-70"> · {crop.variety}</span>}
                    </span>
                </span>
                <span className="text-xs opacity-75 shrink-0" title={stageLabel(crop.stage)}>×{crop.plant_count || 1}</span>
            </div>
        );
    } else {
        // "cell" variant — compact w/ ×N badge in corner
        const hasWarning = (crop.antagonist_warnings || []).length > 0;
        const warningTitle = hasWarning
            ? `Antagonist neighbour${crop.antagonist_warnings.length === 1 ? "" : "s"}: ${crop.antagonist_warnings.map(w => w.neighbor_name).join(", ")}`
            : "";
        body = (
            <div
                ref={setNodeRef}
                {...listeners}
                {...attributes}
                data-testid={`chip-cell-${crop.id}`}
                data-has-warning={hasWarning ? "true" : "false"}
                aria-roledescription={draggable ? "crop piece — drag to move" : "crop piece"}
                aria-label={`${crop.crop_name}, ${crop.plant_count} plants${hasWarning ? ` — ${warningTitle}` : ""}`}
                className={`${base} ${dragCls} ${draggingCls} relative h-full w-full px-2 justify-center gap-1 text-xs font-medium hover:brightness-95 ${hasWarning ? "ring-1 ring-[var(--vm-secondary)]/60" : ""}`}
            >
                <VeggieIcon cropName={crop.crop_name} stage={crop.stage} size={36} className="shrink-0 opacity-95" />
                <span className="truncate max-w-full" title={`${crop.crop_name} · ${stageLabel(crop.stage)}`}>
                    {crop.crop_name}
                </span>
                {hasWarning && (
                    <span
                        className="absolute -top-1 -left-1 flex items-center justify-center w-4 h-4 rounded-full bg-[var(--vm-secondary)] text-[var(--vm-bg)]"
                        title={warningTitle}
                        data-testid={`chip-warning-${crop.id}`}
                        aria-hidden
                    >
                        <AlertTriangle className="w-2.5 h-2.5" strokeWidth={2.5} />
                    </span>
                )}
                {crop.plant_count > 1 && (
                    <span
                        className={`absolute top-0.5 right-1 text-[9px] font-bold px-1 rounded-sm bg-[var(--vm-bg)]/85 ${token.text}`}
                        aria-hidden
                    >
                        ×{crop.plant_count}
                    </span>
                )}
            </div>
        );
    }

    // Wrap body in a click-to-open Popover. The trigger uses asChild so dnd-kit
    // ref + attrs forward through. Click without drag (≤6px distance) opens
    // the popover; full drag never triggers click.
    return (
        <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
            <PopoverTrigger asChild>
                {body}
            </PopoverTrigger>
            <PopoverContent
                side={variant === "palette" ? "left" : "top"}
                align="start"
                className="bg-[var(--vm-card)] border-[var(--vm-border)] p-4 w-auto"
                onOpenAutoFocus={(e) => e.preventDefault()}
            >
                <CropPopoverContent
                    crop={crop}
                    onEdit={() => onEdit?.(crop)}
                    onDelete={() => onDelete?.(crop)}
                />
            </PopoverContent>
        </Popover>
    );
}
